<script lang="ts">
  import {onDestroy} from 'svelte';
  import {router, Router, Route, navigateTo} from 'yrv';

  import {NotificationTypes} from './types/notifications';

  import {HOME, LIST, VIEW_PROJECT} from './constants/views';

  import {notification} from './stores/notifications';
  import {projects} from './stores/projects';
  import {user} from './stores/user';

  import BaseCSS from './BaseCSS.svelte';
  import Header from './components/header/Header.svelte';
  import Notification from './components/notification/Notification.svelte';
  import ListProjects from './components/list-projects/ListProjects.svelte';
  import ViewProject from './components/view-project/ViewProject.svelte';
  import CreateProject from './components/create-project/CreateProject.svelte';
  import EditProject from './components/edit-project/EditProject.svelte';
  import GoogleApiProvider from './components/google-api-provider/GoogleApiProvider.svelte';

  // Force hash routing instead of history API (b/c deployment on GCS)
  // @ts-ignore: Property 'hashchange' does not exist on type 'Writable<RouterState>'
  router.hashchange = true;

  // Update projects (once when subscribed and on each user update)
  // Reset project selection if user logged out
  const unsubscribeUserStore = user.subscribe(async () => {
    await projects.fetchAllProjects();
  });

  onDestroy(unsubscribeUserStore);
</script>

<style lang="postcss">
  .app {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
  }

  .app main {
    position: relative;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    margin: 0.5rem;
  }

  @media (min-width: 640px) {
    .app main {
      margin: 1rem;
    }
  }

  @media (min-width: 768px) {
    .app main {
      margin: 1.5rem;
    }
  }
</style>

<svelte:head>
  <title>Ubilabs Project Gallery</title>
</svelte:head>
<BaseCSS />
<GoogleApiProvider />
<div class="app">
  <Router>
    <Header />

    <main>
      <Notification />
      <Route path={HOME} exact>
        {navigateTo(LIST, {
          replace: true,
          queryParams: $router.query
        })}
      </Route>

      <Route path={LIST}>
        <Router nofallback>
          <Route exact><ListProjects /></Route>

          <Route exact path="/new" disabled={!$user}>
            <CreateProject
              onSuccess={createdProjectId => {
                navigateTo(VIEW_PROJECT, {params: {id: createdProjectId}});
                notification.show(
                  NotificationTypes.SUCCESS,
                  'Success',
                  'The new project was added successfully.'
                );
              }}
              onError={() => {
                notification.show(
                  NotificationTypes.ERROR,
                  'Oops! Something went wrong.',
                  "The project couldn't be saved."
                );
              }} />
          </Route>

          <Route exact path="/:id" let:router>
            <ViewProject id={router.params.id} />
          </Route>

          <Route exact path="/:id/edit" disabled={!$user} let:router>
            <EditProject
              id={router.params.id}
              onSuccess={() => {
                navigateTo(VIEW_PROJECT, {params: router.params});
                notification.show(
                  NotificationTypes.SUCCESS,
                  'Success',
                  'The project was updated successfully.'
                );
              }}
              onError={() => {
                notification.show(
                  NotificationTypes.ERROR,
                  'Oops! Something went wrong.',
                  "The project couldn't be updated."
                );
              }} />
          </Route>
        </Router>
      </Route>

      <!-- TODO: check why fallback route isn't working (https://github.com/pateketrueke/yrv/issues/89) -->
      <!-- <Route fallback>{navigateTo(HOME, {replace: true})}</Route> -->
    </main>
  </Router>
</div>
